import CommonModalBase from '@/components/common/CommonModal'
import CustomInputBase from '@/components/Returns/CustomInput'
import CustomSelectBase from '@/components/Returns/CustomSelect'
import { SECTION_FORM_CONFIG_MAP } from '@/multi-territory/mt-constants/mt-section-form-config'
import {
  fetchSectionBroadheads,
  getBroadheadOptionsForSection,
} from '@/multi-territory/mt-services/mt-section-master-api'
import type {
  MTFormField,
  MTFormFieldOption,
  MTSectionFormConfig,
} from '@/multi-territory/mt-types/mt-section-form-config'
import { Box, Button, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const CommonModal = CommonModalBase as any
const CustomInput = CustomInputBase as any
const CustomSelect = CustomSelectBase as any

// =============================================
// Component
// =============================================

interface MTDynamicSectionFormProps {
  open: boolean
  onClose: () => void
  countryCode: string
  onSubmit?: (formData: Record<string, string>) => void
}

export function MTDynamicSectionForm({
  open,
  onClose,
  countryCode,
  onSubmit,
}: MTDynamicSectionFormProps) {
  const [formValues, setFormValues] = useState<Record<string, string>>({})
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  // Dynamic options loaded via depends_on
  const [dynamicOptions, setDynamicOptions] = useState<
    Record<string, MTFormFieldOption[]>
  >({})
  // Loading state for dependent fields
  const [optionsLoading, setOptionsLoading] = useState<Record<string, boolean>>(
    {}
  )
  // Track fields that were auto-selected (single option) to disable them
  const [autoSelectedFields, setAutoSelectedFields] = useState<
    Record<string, boolean>
  >({})

  // Cache for broadhead map (fetched once from API)
  const broadheadMapRef = useRef<Record<string, string[]> | null>(null)

  // Get config based on country
  const formConfig: MTSectionFormConfig | null = useMemo(() => {
    return SECTION_FORM_CONFIG_MAP[countryCode] || null
  }, [countryCode])

  // Sort fields by order
  const sortedFields = useMemo(() => {
    if (!formConfig) return []
    return [...formConfig.fields].sort((a, b) => a.order - b.order)
  }, [formConfig])

  // Initialize form values on open or country change
  useEffect(() => {
    if (open && formConfig) {
      const initialValues: Record<string, string> = {}
      for (const field of formConfig.fields) {
        if (
          field.auto_fill &&
          field.auto_fill.trigger === 'onLoad' &&
          field.auto_fill.default_value != null
        ) {
          initialValues[field.field_key] = String(field.auto_fill.default_value)
        } else {
          initialValues[field.field_key] = ''
        }
      }
      setFormValues(initialValues)
      setFormErrors({})
      setDynamicOptions({})
      setOptionsLoading({})
      setAutoSelectedFields({})
      broadheadMapRef.current = null
    }
  }, [open, formConfig])

  /**
   * Fetches dependent options from BE based on depends_on config.
   * Uses the actual API for broadheads.
   */
  const fetchDependentOptionsFromApi = useCallback(
    async (
      field: MTFormField,
      sourceValue: string
    ): Promise<MTFormFieldOption[]> => {
      if (!field.depends_on) return []

      const { response_format } = field.depends_on

      if (response_format === 'key_value_map') {
        // Fetch full broadhead map from API (cache it)
        if (!broadheadMapRef.current) {
          const resp = await fetchSectionBroadheads()
          if (resp.success) {
            broadheadMapRef.current = resp.data
          } else {
            return []
          }
        }
        return getBroadheadOptionsForSection(
          broadheadMapRef.current!,
          sourceValue
        )
      }

      // For other response formats, extend here in future
      return []
    },
    []
  )

  // Handle depends_on: load options when source field changes
  const handleDependsOn = useCallback(
    async (changedKey: string, newValue: string) => {
      if (!formConfig) return

      const dependentFields = formConfig.fields.filter(
        f => f.depends_on && f.depends_on.source === changedKey
      )

      for (const field of dependentFields) {
        if (!field.depends_on) continue

        // Clear dependent field value when source changes
        if (field.depends_on.clear_on_change) {
          setFormValues(prev => ({ ...prev, [field.field_key]: '' }))
          setAutoSelectedFields(prev => ({ ...prev, [field.field_key]: false }))
        }

        if (!newValue) {
          // Source cleared — clear options
          setDynamicOptions(prev => ({ ...prev, [field.field_key]: [] }))
          setAutoSelectedFields(prev => ({ ...prev, [field.field_key]: false }))
          continue
        }

        // Set loading
        setOptionsLoading(prev => ({ ...prev, [field.field_key]: true }))

        try {
          const options = await fetchDependentOptionsFromApi(field, newValue)
          setDynamicOptions(prev => ({ ...prev, [field.field_key]: options }))

          // Auto-select if only 1 option and auto_select_single is enabled
          if (field.depends_on.auto_select_single && options.length === 1) {
            setFormValues(prev => ({
              ...prev,
              [field.field_key]: options[0].value,
            }))
            setAutoSelectedFields(prev => ({
              ...prev,
              [field.field_key]: true,
            }))
            // Clear any error for this field
            setFormErrors(prev => {
              const next = { ...prev }
              delete next[field.field_key]
              return next
            })
          } else {
            setAutoSelectedFields(prev => ({
              ...prev,
              [field.field_key]: false,
            }))
          }
        } finally {
          setOptionsLoading(prev => ({ ...prev, [field.field_key]: false }))
        }
      }
    },
    [formConfig, fetchDependentOptionsFromApi]
  )

  // Handle auto-fill when a source field changes
  const handleAutoFill = useCallback(
    async (changedKey: string, newValue: string) => {
      if (!formConfig) return

      const dependentFields = formConfig.fields.filter(
        f =>
          f.auto_fill &&
          f.auto_fill.trigger === 'onChange' &&
          f.auto_fill.source === changedKey
      )

      for (const field of dependentFields) {
        if (field.auto_fill?.api && newValue) {
          // In production, call the actual auto-fill API here
          // For now, clear the value (BE will provide this logic)
          // const result = await fetchAutoFillFromApi(field.auto_fill.api, newValue)
          // setFormValues(prev => ({ ...prev, [field.field_key]: result }))
        } else {
          setFormValues(prev => ({ ...prev, [field.field_key]: '' }))
        }
      }
    },
    [formConfig]
  )

  // Handle field change
  const handleChange = (fieldKey: string, value: string) => {
    setFormValues(prev => ({ ...prev, [fieldKey]: value }))

    // Clear error
    if (formErrors[fieldKey]) {
      setFormErrors(prev => {
        const next = { ...prev }
        delete next[fieldKey]
        return next
      })
    }

    // Trigger depends_on (load child options)
    handleDependsOn(fieldKey, value)

    // Trigger auto-fill for dependent fields
    handleAutoFill(fieldKey, value)
  }

  // Handle date change
  const handleDateChange = (fieldKey: string, date: Dayjs | null) => {
    const value = date ? date.format('DD/MM/YYYY') : ''
    handleChange(fieldKey, value)
  }

  // Validate form
  const validateForm = (): boolean => {
    if (!formConfig) return false

    const errors: Record<string, string> = {}

    for (const field of formConfig.fields) {
      if (!field.visible) continue

      const value = formValues[field.field_key]?.trim() || ''

      // Required check
      if (field.required && !value) {
        errors[field.field_key] =
          field.validations.required_message || `${field.label} is required`
        continue
      }

      if (!value) continue

      // Number validations
      if (field.type === 'number') {
        const numVal = parseFloat(value)
        if (isNaN(numVal)) {
          errors[field.field_key] = `${field.label} must be a valid number`
          continue
        }
        if (
          field.validations.min !== undefined &&
          numVal < field.validations.min
        ) {
          errors[field.field_key] =
            `${field.label} must be at least ${field.validations.min}`
          continue
        }
        if (
          field.validations.max !== undefined &&
          numVal > field.validations.max
        ) {
          errors[field.field_key] =
            `${field.label} cannot exceed ${field.validations.max}`
          continue
        }
      }

      // Regex validation
      if (field.validations.regex) {
        const regex = new RegExp(field.validations.regex)
        if (!regex.test(value)) {
          errors[field.field_key] =
            field.validations.regex_message ||
            `Please enter a valid ${field.label}`
          continue
        }
      }
    }

    // Cross-field date validations
    for (const field of formConfig.fields) {
      if (field.type !== 'date') continue
      const value = formValues[field.field_key]
      if (!value) continue

      if (field.validations.max_date) {
        const maxFieldKey = field.validations.max_date
        const maxValue = formValues[maxFieldKey]
        if (maxValue) {
          const current = dayjs(value, 'DD/MM/YYYY')
          const max = dayjs(maxValue, 'DD/MM/YYYY')
          if (current.isAfter(max)) {
            errors[field.field_key] =
              `${field.label} must be before ${formConfig.fields.find(f => f.field_key === maxFieldKey)?.label || maxFieldKey}`
          }
        }
      }

      if (field.validations.min_date) {
        const minFieldKey = field.validations.min_date
        const minValue = formValues[minFieldKey]
        if (minValue) {
          const current = dayjs(value, 'DD/MM/YYYY')
          const min = dayjs(minValue, 'DD/MM/YYYY')
          if (current.isBefore(min)) {
            errors[field.field_key] =
              `${field.label} must be after ${formConfig.fields.find(f => f.field_key === minFieldKey)?.label || minFieldKey}`
          }
        }
      }
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Submit
  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      onSubmit?.(formValues)
      handleClose()
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormValues({})
    setFormErrors({})
    setDynamicOptions({})
    setAutoSelectedFields({})
    onClose()
  }

  // Get options for a field — prioritize dynamic (depends_on) over static
  const getFieldOptions = (field: MTFormField): MTFormFieldOption[] => {
    if (field.depends_on) {
      return dynamicOptions[field.field_key] || []
    }
    return field.options || []
  }

  // --- Render individual field ---
  const renderField = (field: MTFormField) => {
    if (!field.visible) return null

    const value = formValues[field.field_key] || ''
    const error = formErrors[field.field_key]
    const isDisabled = !field.editable
    const isLoading = optionsLoading[field.field_key] || false

    switch (field.type) {
      case 'dropdown': {
        const options = getFieldOptions(field)
        // Disable if depends_on and source not selected yet
        const sourceNotSelected =
          field.depends_on && !formValues[field.depends_on.source]
        // Disable if auto-selected (single option)
        const isAutoSelected = autoSelectedFields[field.field_key] || false
        const dropdownDisabled =
          isDisabled || isLoading || !!sourceNotSelected || isAutoSelected

        return (
          <CustomSelect
            key={field.field_key}
            label={field.label}
            required={field.required}
            value={value}
            onChange={(e: { target: { value: string } }) =>
              handleChange(field.field_key, e.target.value)
            }
            options={options}
            getOptionLabel={(opt: MTFormFieldOption) => opt.label}
            getOptionValue={(opt: MTFormFieldOption) => opt.value}
            placeholder={isLoading ? 'Loading...' : field.placeholder}
            disabled={dropdownDisabled}
            error={!!error}
            helperText={error || ''}
            fullWidth
          />
        )
      }

      case 'date':
        return (
          <LocalizationProvider
            key={field.field_key}
            dateAdapter={AdapterDayjs}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: 'var(--forbrand-primary)',
                  mb: 1,
                  lineHeight: '140%',
                }}
              >
                {field.label}
                {field.required && (
                  <span style={{ color: 'var(--forbrand-error)' }}>*</span>
                )}
              </Typography>
              <DatePicker
                value={value ? dayjs(value, 'DD/MM/YYYY') : null}
                onChange={(date: Dayjs | null) =>
                  handleDateChange(field.field_key, date)
                }
                disabled={isDisabled}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: 'medium',
                    fullWidth: true,
                    placeholder: field.placeholder,
                    error: !!error,
                    helperText: error || '',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDisabled
                          ? 'var(--bg-disabled)'
                          : '#fff',
                        fontSize: '14px',
                        height: '48px',
                        borderRadius: 'unset',
                        '& fieldset': {
                          borderColor: error
                            ? 'var(--forbrand-error)'
                            : 'var(--forbrand-primary)',
                        },
                        '&:hover fieldset': {
                          borderColor: error
                            ? 'var(--forbrand-error)'
                            : 'var(--forbrand-primary)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: error
                            ? 'var(--forbrand-error)'
                            : 'var(--forbrand-color)',
                        },
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#999',
                        opacity: 1,
                      },
                    },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        )

      case 'number':
        return (
          <CustomInput
            key={field.field_key}
            label={field.label}
            required={field.required}
            placeholder={field.placeholder}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value
              if (val === '' || /^[0-9]*\.?[0-9]*$/.test(val)) {
                handleChange(field.field_key, val)
              }
            }}
            disabled={isDisabled}
            error={!!error}
            helperText={error || ''}
            fullWidth
          />
        )

      case 'text':
        return (
          <CustomInput
            key={field.field_key}
            label={field.label}
            required={field.required}
            placeholder={field.placeholder}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(field.field_key, e.target.value)
            }
            disabled={isDisabled}
            error={!!error}
            helperText={error || ''}
            fullWidth
          />
        )

      case 'hidden':
        return null

      default:
        return null
    }
  }

  if (!formConfig) {
    return (
      <CommonModal
        open={open}
        onClose={handleClose}
        title="Error"
        maxWidth="sm"
      >
        <Typography color="error">
          No form configuration found for country: {countryCode}
        </Typography>
      </CommonModal>
    )
  }

  // Split fields into two columns
  const col1Fields = sortedFields.filter(f => f.grid_column === 1 && f.visible)
  const col2Fields = sortedFields.filter(f => f.grid_column === 2 && f.visible)
  const maxRows = Math.max(col1Fields.length, col2Fields.length)

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={formConfig.form_title}
      maxWidth="md"
    >
      <>
        <Typography
          variant="body2"
          sx={{ color: 'var(--forbrand-error, red)', mb: 3, fontSize: '14px' }}
        >
          (*) Fields are mandatory
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 3,
            mb: 3,
          }}
        >
          {Array.from({ length: maxRows }).map((_, rowIdx) => {
            const leftField = col1Fields[rowIdx]
            const rightField = col2Fields[rowIdx]
            return (
              <Box key={rowIdx} sx={{ display: 'contents' }}>
                <Box>{leftField && renderField(leftField)}</Box>
                <Box>{rightField && renderField(rightField)}</Box>
              </Box>
            )
          })}
        </Box>

        {/* Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
          <Button
            variant="contained"
            disabled={loading}
            onClick={handleSubmit}
            sx={{
              backgroundColor: 'var(--forbrand-bg-variant, #e8873a)',
              color: 'var(--forbrand-brand-varient, #000)',
              textTransform: 'none',
              px: '24px',
              py: '8px',
              fontSize: '16px',
              '&:hover': {
                backgroundColor: '#d4762e',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
              },
            }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </>
    </CommonModal>
  )
}
